import { lazy, Suspense } from "react";
import { lazyWithReload } from "@/lib/lazyWithReload";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminProtectedRoute } from "@/components/admin/AdminProtectedRoute";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ResetPassword from "./pages/ResetPassword";
import Canvas from "./pages/Canvas";
import SharePage from "./pages/SharePage";
import NotFound from "./pages/NotFound";
import Onboarding from "./pages/Onboarding";

// Admin pages load on demand: only one person uses them, and they bring recharts
const AdminOverview = lazyWithReload(() => import("./pages/admin/AdminOverview"));
const AdminUsers = lazyWithReload(() => import("./pages/admin/AdminUsers"));
const AdminWaitlist = lazyWithReload(() => import("./pages/admin/AdminWaitlist"));
const AdminStorage = lazyWithReload(() => import("./pages/admin/AdminStorage"));
const AdminAIUsage = lazyWithReload(() => import("./pages/admin/AdminAIUsage"));
const AdminSettings = lazyWithReload(() => import("./pages/admin/AdminSettings"));
const AdminErrorReporting = lazyWithReload(() => import("./pages/admin/AdminErrorReporting"));
const AdminAuditLog = lazyWithReload(() => import("./pages/admin/AdminAuditLog"));

// Dev-only pages. `import.meta.env.DEV` is a build-time constant, so in
// production these imports are dead code and the pages never reach the bundle.
const PartnrKitGallery = import.meta.env.DEV ? lazy(() => import("./pages/dev/PartnrKitGallery")) : null;
const BrandFixture = import.meta.env.DEV ? lazy(() => import("./pages/dev/BrandFixture")) : null;
const BrandLongTail = import.meta.env.DEV ? lazy(() => import("./pages/dev/BrandLongTail")) : null;
const PartnrAgentDev = import.meta.env.DEV ? lazy(() => import("./pages/dev/PartnrAgentDev")) : null;
const DocCanvasDev = import.meta.env.DEV ? lazy(() => import("./pages/dev/DocCanvasDev")) : null;
const PartnrPanelDev = import.meta.env.DEV ? lazy(() => import("./pages/dev/PartnrPanelDev")) : null;
const PartnrGhostsDev = import.meta.env.DEV ? lazy(() => import("./pages/dev/PartnrGhostsDev")) : null;
const MobileLibraryDev = import.meta.env.DEV ? lazy(() => import("./pages/dev/MobileLibraryDev")) : null;
const FolderFixture = import.meta.env.DEV ? lazy(() => import("./pages/dev/FolderFixture")) : null;
const LogoGallery = import.meta.env.DEV ? lazy(() => import("./pages/dev/LogoGallery")) : null;
const ArrowFixture = import.meta.env.DEV ? lazy(() => import("./pages/dev/ArrowFixture")) : null;

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route 
              path="/canvas" 
              element={
                <ProtectedRoute>
                  <Canvas />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route path="/share/:token" element={<SharePage />} />
            <Route path="/share/board/:token" element={<SharePage shareType="board" />} />
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminProtectedRoute><Suspense fallback={null}><AdminOverview /></Suspense></AdminProtectedRoute>} />
            <Route path="/admin/users" element={<AdminProtectedRoute><Suspense fallback={null}><AdminUsers /></Suspense></AdminProtectedRoute>} />
            <Route path="/admin/waitlist" element={<AdminProtectedRoute><Suspense fallback={null}><AdminWaitlist /></Suspense></AdminProtectedRoute>} />
            <Route path="/admin/storage" element={<AdminProtectedRoute><Suspense fallback={null}><AdminStorage /></Suspense></AdminProtectedRoute>} />
            <Route path="/admin/ai-usage" element={<AdminProtectedRoute><Suspense fallback={null}><AdminAIUsage /></Suspense></AdminProtectedRoute>} />
            <Route path="/admin/error-reports" element={<AdminProtectedRoute><Suspense fallback={null}><AdminErrorReporting /></Suspense></AdminProtectedRoute>} />
            <Route path="/admin/audit" element={<AdminProtectedRoute><Suspense fallback={null}><AdminAuditLog /></Suspense></AdminProtectedRoute>} />
            <Route path="/admin/settings" element={<AdminProtectedRoute><Suspense fallback={null}><AdminSettings /></Suspense></AdminProtectedRoute>} />
            {PartnrKitGallery && (
              <Route path="/dev/partnr-kit" element={<Suspense fallback={null}><PartnrKitGallery /></Suspense>} />
            )}
            {BrandFixture && (
              <Route path="/dev/brand-fixture" element={<Suspense fallback={null}><BrandFixture /></Suspense>} />
            )}
            {FolderFixture && (
              <Route path="/dev/folders" element={<Suspense fallback={null}><FolderFixture /></Suspense>} />
            )}
            {ArrowFixture && (
              <Route path="/dev/arrows" element={<Suspense fallback={null}><ArrowFixture /></Suspense>} />
            )}
            {LogoGallery && (
              <Route path="/dev/logos" element={<Suspense fallback={null}><LogoGallery /></Suspense>} />
            )}
            {BrandLongTail && (
              <Route path="/dev/brand-longtail" element={<Suspense fallback={null}><BrandLongTail /></Suspense>} />
            )}
            {PartnrAgentDev && (
              <Route path="/dev/partnr-agent" element={<Suspense fallback={null}><PartnrAgentDev /></Suspense>} />
            )}
            {DocCanvasDev && (
              <Route path="/dev/doc-canvas" element={<Suspense fallback={null}><DocCanvasDev /></Suspense>} />
            )}
            {PartnrPanelDev && (
              <Route path="/dev/partnr-panel" element={<Suspense fallback={null}><PartnrPanelDev /></Suspense>} />
            )}
            {PartnrGhostsDev && (
              <Route path="/dev/partnr-ghosts" element={<Suspense fallback={null}><PartnrGhostsDev /></Suspense>} />
            )}
            {MobileLibraryDev && (
              <Route path="/dev/mobile-library" element={<Suspense fallback={null}><MobileLibraryDev /></Suspense>} />
            )}
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
